import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { SupportAgentDraft } from "./api";
import type { CorrectionScope, SupportDraftFailure } from "./supportWorkbenchApi";
import type { RevisionHead } from "./draftRevisions";

export interface DraftTranslationPreview {
  sourceText: string;
  translatedText: string;
  targetLang: string;
  riskMarkers: string[];
}

export type DraftDeliveryState = "editable" | "delivery_unknown";

export interface DraftAttachment {
  id: string;
  name: string;
  mime: string;
  size: number;
  returnId: string | null;
}

export interface ThreadDraft {
  conversationPendingDraft?: {
    requestId: string;
    currentDraft: string;
    attachmentIds: string[];
    includeImages: boolean;
  } | null;
  /** The last private-chat proposal was undone; survives reload until the next turn. */
  conversationRestoredDraft?: boolean;
  correctionHeadId?: string | null;
  /** Head of the server revision chain (support.archive only); survives reload. */
  revisionHead?: RevisionHead | null;
  agentFailure?: SupportDraftFailure | null;
  pendingAgentRequest?: PendingAgentRequest | null;
  agentResult?: SupportAgentDraft | null;
  agentCorrection?: string;
  reviewOpen?: boolean;
  selectedAgentId?: string | null;
  includeImages?: boolean;
  translationPreview?: DraftTranslationPreview | null;
  attachments?: DraftAttachment[];
  auditId?: string;
  threadId: string;
  text: string;
  idempotencyKey: string;
  translate: boolean;
  targetLang: string;
  translationPreference?: "auto" | "manual";
  deliveryState: DraftDeliveryState;
  updatedAt: string;
}

export interface PendingAgentRequest {
  requestId: string;
  threadId: string;
  profileId: string;
  includeImages: boolean;
  draftText: string;
  correction?: string;
  correctionScope?: CorrectionScope;
  attachmentIds: string[];
  previousAuditId?: string;
}

interface MessagesDraftDb extends DBSchema {
  drafts: {
    key: string;
    value: ThreadDraft;
  };
}

let database: Promise<IDBPDatabase<MessagesDraftDb>> | null = null;
const memoryFallback = new Map<string, ThreadDraft>();
const RECOVERY_PREFIX = "customy-message-draft-recovery:";

// IndexedDB writes may still be pending when a refresh destroys this document.
// Keep only that pending snapshot synchronously; remove it once IDB commits.
function stageRecovery(key: string, draft: ThreadDraft | null): string | null {
  try {
    const value = JSON.stringify({ draft });
    window.localStorage.setItem(RECOVERY_PREFIX + key, value);
    return value;
  } catch {
    return null;
  }
}

function clearRecovery(key: string, value: string | null) {
  try {
    if (value !== null && window.localStorage.getItem(RECOVERY_PREFIX + key) === value)
      window.localStorage.removeItem(RECOVERY_PREFIX + key);
  } catch {
    /* Storage may be unavailable in private/restricted contexts. */
  }
}

function scopedDraftKey(userId: string, threadId: string) {
  return `${userId}:${threadId}`;
}

function getDatabase() {
  if (typeof indexedDB === "undefined") return null;
  if (!database) {
    database = openDB<MessagesDraftDb>("customy-message-drafts-v2", 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("drafts")) db.createObjectStore("drafts");
      },
    }).catch((error) => {
      database = null;
      throw error;
    });
  }
  return database;
}

export function createReplyIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  if (typeof crypto === "undefined") {
    throw new Error("Sichere Browser-Zufallsquelle ist nicht verfügbar");
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return `draft-${Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("")}`;
}

export async function readThreadDraft(
  userId: string,
  threadId: string,
): Promise<ThreadDraft | null> {
  const key = scopedDraftKey(userId, threadId);
  try {
    const raw = window.localStorage.getItem(RECOVERY_PREFIX + key);
    if (raw) {
      const recovery = JSON.parse(raw);
      if (recovery.draft === null || recovery.draft?.threadId === threadId) return recovery.draft;
    }
  } catch {
    /* Fall through to the committed snapshot. */
  }
  const db = getDatabase();
  if (!db) return memoryFallback.get(key) || null;
  return (await db).get("drafts", key).then((value) => value || null);
}

export async function writeThreadDraft(userId: string, draft: ThreadDraft): Promise<void> {
  const key = scopedDraftKey(userId, draft.threadId);
  const recovery = stageRecovery(key, draft);
  const db = getDatabase();
  if (!db) {
    memoryFallback.set(key, draft);
    return;
  }
  await (await db).put("drafts", draft, key);
  clearRecovery(key, recovery);
}

export async function deleteThreadDraft(userId: string, threadId: string): Promise<void> {
  const key = scopedDraftKey(userId, threadId);
  const recovery = stageRecovery(key, null);
  const db = getDatabase();
  if (!db) {
    memoryFallback.delete(key);
    return;
  }
  await (await db).delete("drafts", key);
  clearRecovery(key, recovery);
}

function resolveActionStorageKey(userId: string, threadId: string) {
  return `customy-message-resolve-key:${userId}:${threadId}`;
}

export function getThreadResolveIdempotencyKey(userId: string, threadId: string): string {
  const storageKey = resolveActionStorageKey(userId, threadId);
  const existing = window.localStorage.getItem(storageKey);
  if (existing) return existing;
  const created = createReplyIdempotencyKey();
  window.localStorage.setItem(storageKey, created);
  return created;
}

export function clearThreadResolveIdempotencyKey(userId: string, threadId: string): void {
  window.localStorage.removeItem(resolveActionStorageKey(userId, threadId));
}

export async function clearUserMessageDrafts(userId: string): Promise<void> {
  if (!userId) return;
  const prefix = `${userId}:`;
  // Clear synchronous recovery even if IndexedDB later fails during logout.
  try {
    for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(RECOVERY_PREFIX + prefix)) window.localStorage.removeItem(key);
    }
  } catch {
    /* IndexedDB cleanup still runs below. */
  }
  for (const key of [...memoryFallback.keys()]) {
    if (key.startsWith(prefix)) memoryFallback.delete(key);
  }
  const db = getDatabase();
  if (db) {
    const opened = await db;
    const keys = await opened.getAllKeys("drafts");
    await Promise.all(
      keys
        .filter((key) => String(key).startsWith(prefix))
        .map((key) => opened.delete("drafts", key)),
    );
  }
  const resolvePrefix = `customy-message-resolve-key:${userId}:`;
  for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
    const key = window.localStorage.key(index);
    if (key?.startsWith(resolvePrefix)) window.localStorage.removeItem(key);
  }
}
